import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { store } from './store';
import { fetchOperationsData } from './store/slices/operationsSlice';
import { DeviceProvider } from './context/DeviceContext';
import { OperationRedirector } from './components/operations/OperationRedirector';
import { DeviceDebug } from './components/debug/DeviceDebug';
import LoginPage from './pages/auth/LoginPage';
import MainLayout from './components/layout/MainLayout';
import DashboardPage from './pages/dashboard/DashboardPage';
import RecargasPage from './pages/operations/RecargasPage';
import RecargasPageSteps from './pages/operations/RecargasPageSteps';
import PaquetesPage from './pages/operations/PaquetesPage';
import PaquetesPageSteps from './pages/operations/PaquetesPageSteps';
import FacturasPage from './pages/operations/FacturasPage';
import FacturasPageSteps from './pages/operations/FacturasPageSteps';
import SegurosPage from './pages/operations/SegurosPage';
import SegurosPageSteps from './pages/operations/SegurosPageSteps';
import TransactionsPage from './pages/transactions/TransactionsPage';
import WalletPage from './pages/wallet/WalletPage';
import ProfilePage from './pages/profile/ProfilePage';
import SettingsPage from './pages/settings/SettingsPage';
import NotFoundPage from './pages/NotFoundPage';

// Componente para rutas protegidas que requieren autenticación
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuth = !!localStorage.getItem('token');
  
  if (!isAuth) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  useEffect(() => {
    // Verificar token y validez de sesión
    const token = localStorage.getItem('token');
    if (token) {
      console.log('Token encontrado en localStorage. Sesión activa.');
      // Cargar datos de operaciones al inicio cuando hay una sesión activa
      store.dispatch(fetchOperationsData());
    }
  }, []);

  return (
    <Provider store={store}>
      <DeviceProvider>
        <Router>
          <Routes>
            {/* Rutas públicas */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Rutas protegidas */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <DashboardPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Operaciones */}
            <Route
              path="/operaciones/recargas"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <RecargasPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Nueva versión por pasos de Recargas */}
            <Route
              path="/operaciones/recargas-wizard"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <RecargasPageSteps />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operaciones/paquetes"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PaquetesPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Nueva versión por pasos de Paquetes */}
            <Route
              path="/operaciones/paquetes-wizard"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PaquetesPageSteps />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operaciones/facturas"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <FacturasPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Nueva versión por pasos de Facturas */}
            <Route
              path="/operaciones/facturas-wizard"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <FacturasPageSteps />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operaciones/seguros"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <SegurosPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/operaciones/seguros-wizard"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <SegurosPageSteps />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Rutas para redireccionamiento inteligente según dispositivo */}
            <Route
              path="/operaciones/recargas-auto"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <OperationRedirector operationType="recargas" />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operaciones/paquetes-auto"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <OperationRedirector operationType="paquetes" />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operaciones/facturas-auto"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <OperationRedirector operationType="facturas" />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operaciones/seguros-auto"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <OperationRedirector operationType="seguros" />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Transacciones */}
            <Route
              path="/transacciones"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TransactionsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Wallet */}
            <Route
              path="/wallet"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <WalletPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Perfil */}
            <Route
              path="/perfil"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProfilePage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            {/* Configuración */}
            <Route
              path="/configuracion"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <SettingsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Redireccionar a login si la ruta es raíz */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* Página 404 para rutas no encontradas */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Router>
      </DeviceProvider>
    </Provider>
  );
};

export default App;
